import {useTheme} from "@emotion/react";
import {useLoaderData, useNavigation} from "@remix-run/react";
import CardBib from "../../assets/images/PushMailCard/cardBib.png";
import PushMailLogo from "../../assets/images/PushMailCard/pmLogo.png";
import {BASE_AD_TAG} from "../../components/adSections/index.jsx";
import {getLinkType} from "../../constants/index.js";
import useHasHydrated from "../../hooks/useHasHydrated.js";
import {getMagazineUrlByLanguage} from "../../layouts/Header";
import {theme} from "../../styles/theme.js";
import {getGtagPageName} from "../../utils/getGtagPageName";
import {getImageProperty} from "../../utils/getImageProperty.js";
import * as gtag from "../../utils/gtag.client";
import CTA from "../CTA";
import {Link} from "../link";
import {Text} from "../text";
import {Content} from "./Content.jsx";
import {ImgContent} from "./ImgContent.jsx";
import {Type} from "./Type.jsx";
import {
  BgImage,
  BibImg,
  Card,
  ContentInnerWrapper,
  LogoImg,
  LogoWrapper,
  PushMailContainer,
  PushMailWrapper,
  TextWrapper,
} from "./styles.js";

const CrossContentCard = ({
  cardData,
  isDestinationCard,
  styles,
  country,
  isDestinationPage = false,
}) => {
  const hasHydrated = useHasHydrated();

  const navigation = useNavigation();
  const {language, translation} = useLoaderData();
  const pushMailSubtitle = translation?.pushMailSubtitle;
  const pushMailTitle = translation?.pushMailTitle;
  const ctaText = translation?.pushMailCta;
  const ctaLink = getMagazineUrlByLanguage(language);
  const {breakpoints} = useTheme();
  const {sm, xs, md, isMobile, xl} = breakpoints;

  const imageUrl = getImageProperty({isMobile, cardData, property: "url"});
  const altImgText = getImageProperty({isMobile, cardData, property: "alt"}) || "";
  const imgTitle = getImageProperty({isMobile, cardData, property: "title"}) || "";
  const isImg = !!imageUrl;

  const formatName = cardData?.taxonomies?.post_cat?.[0]?.name;
  const date = cardData?.acf?.left_side?.date;
  const postTitle = cardData?.post_title;
  const themeColor = cardData?.taxonomies?.thematic?.[0]?.acf?.color;
  const themeText = cardData?.taxonomies?.thematic?.[0]?.name;
  const themeSlug = cardData?.taxonomies?.thematic?.[0]?.slug;
  const slug = cardData?.taxonomies?.post_cat?.[0]?.slug;
  const isSponsored = cardData?.acf?.sponsored === "1";
  const showPushMail = !!cardData?.isPushmailCard;
  const showAd = !!cardData?.isAd;

  const designType = isSponsored
    ? "blue"
    : slug === "fiche-pratique"
      ? "blue-border"
      : "white";
  const source = cardData?.taxonomies?.source?.[0];
  // const linkType = getLinkType(cardData?.front_links?.endpoint, language);
  // const slugLink = cardData?.front_links?.slug;
  const link = cardData?.front_links?.relative_link;
  if (navigation.state === "loading") {
    return;
  }
  return (
    <div
      key={cardData?.id}
      style={{
        textDecoration: "none",
        height: "100%",
        width: "100%",
        ...(hasHydrated ? styles : {}),
      }}
      role="button"
      tabIndex={0}
    >
      {showAd ? (
        <Card id={BASE_AD_TAG.card} isImg={null} designType={"ad"} />
      ) : showPushMail ? (
        <Card
          styles={{cursor: "default"}}
          isImg={showPushMail ? null : isImg}
          designType={designType}
        >
          <PushMailWrapper>
            <BgImage>
              <PushMailContainer>
                <LogoWrapper>
                  <LogoImg alt="logo" src={PushMailLogo} />
                  <Text styles={{color: "#1D3140"}} type="tag">
                    {pushMailSubtitle}
                  </Text>
                </LogoWrapper>
                <Text
                  styles={{color: "#1D3140", marginBottom: xs ? 24 : sm ? "15%" : 24}}
                  type="textLarge"
                >
                  {pushMailTitle}
                </Text>

                <CTA
                  textStyles={{
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    maxWidth: xs ? "auto" : sm ? "auto" : md ? "auto" : xl ? 150 : "auto",
                  }}
                  buttonStyles={{marginBottom: 40, alignSelf: "flex-start"}}
                  variant="viaMichelin"
                  size="medium"
                  buttonColor={theme?.colors?.brandLightBlue}
                  type="primary"
                  to={ctaLink}
                  onClick={() => {
                    setTimeout(() => {
                      scrollTo(0, 0);
                    }, 140);
                    gtag.event({
                      event: "redirection_vm",
                      action: "click",
                      page_name: getGtagPageName(window.location.pathname),
                    });
                  }}
                >
                  {ctaText}
                </CTA>
              </PushMailContainer>
              <BibImg alt="bib" src={CardBib} />
            </BgImage>
          </PushMailWrapper>
        </Card>
      ) : (
        <Link
          styles={{whiteSpace: "normal"}}
          to={link}
          outerStyles={{height: "100%"}}
          onClick={() => {
            setTimeout(() => {
              scrollTo(0, 0);
            }, 140);
            const commonEventData = {
              event: "article",
              action: "selection",
              article_type: formatName,
              source: cardData?.taxonomies?.source?.[0]?.name,
              tag_article: themeText,
              page_name: getGtagPageName(window.location.pathname),
              country: "france",
            };

            const eventData = isDestinationPage
              ? {...commonEventData, country}
              : commonEventData;

            gtag.event(eventData);
          }}
        >
          {isDestinationCard ? (
            <Card isImg={false}>
              <ImgContent
                isDestinationCard={isDestinationCard}
                isImg={isImg}
                imageUrl={imageUrl}
                ImgTitle={imgTitle?.title || ""}
                altImgText={altImgText?.alt || ""}
              />
              <TextWrapper>
                <Text
                  styles={{
                    color: "#FFFFFF",
                    textAlign: "center",
                    mark: {
                      color: "white",
                      backgroundColor: "transparent",
                    },
                  }}
                  type="titleSmall"
                  dangerouslySetInnerHTML={{
                    __html: cardData?.post_title,
                  }}
                />
              </TextWrapper>
            </Card>
          ) : (
            <Card isImg={showPushMail ? null : isImg} designType={designType}>
              {!!source && <Type source={source} />}
              <ImgContent
                isImg={isImg}
                imageUrl={imageUrl}
                ImgTitle={imgTitle}
                altImgText={altImgText}
                postTitle={postTitle}
              />
              {isImg ? (
                <Content
                  themeSlug={themeSlug}
                  formatName={formatName}
                  postTitle={postTitle}
                  date={date}
                  designType={designType}
                  isImg={isImg}
                  themeColor={themeColor}
                  themeText={themeText}
                />
              ) : (
                <ContentInnerWrapper>
                  <Content
                    themeSlug={themeSlug}
                    themeText={themeText}
                    formatName={formatName}
                    postTitle={postTitle}
                    date={date}
                    designType={designType}
                    isImg={isImg}
                    themeColor={themeColor}
                  />
                </ContentInnerWrapper>
              )}
            </Card>
          )}
        </Link>
      )}
    </div>
  );
};

export default CrossContentCard;
